<script setup>
import { computed, ref, onMounted } from "vue";
import { apiQuery } from "@/api/j24api";

const loading = ref(true);
const groups = ref([]);
const roles = ref([]);
const permissions = ref([]);
const customers = ref([]);
const customerGroups = ref([]);
const searchQuery = ref("");

// Edit/Add state
const isEditing = ref(false);
const editGroupRecord = ref({
  _id: "",
  name: "",
  groupID: "",
  roles: [],
  permissions: [],
});

// Dialog states
const addPermissionDialog = ref({
  show: false,
  app: "",
  scopeType: "customer", // "customer" or "customerGroup"
  scope: "",
  scopeGroup: "",
  selected: "",
  selectedActions: [],
});

const addRoleDialog = ref({
  show: false,
  scopeType: "customer", // "customer" or "customerGroup"
  scope: "",
  scopeGroup: "",
  selected: "",
});

// Computed properties
const apps = computed(() => [...new Set(permissions.value.map((p) => p.app))]);

const permissionsFiltered = computed(() =>
  permissions.value.filter((p) => p.app === addPermissionDialog.value.app)
);

const availableActions = computed(() => {
  const permission = permissions.value.find(
    (p) =>
      p.app === addPermissionDialog.value.app &&
      p.permissionName === addPermissionDialog.value.selected
  );
  return permission?.actions || [];
});

// Helper functions
const getRoleName = (id) => {
  const role = roles.value.find((r) => r._id === id);
  return role?.name || "Role Not Found (has it been deleted?)";
};

const getCustomerGroupName = (id) => {
  const group = customerGroups.value.find((g) => g._id === id);
  return group?.name || "Customer Group Not Found";
};

const getScopeDisplay = (item) => {
  if (item.scopeGroup) {
    return `[Group] ${getCustomerGroupName(item.scopeGroup)}`;
  }
  return item.scope || "N/A";
};

const resetDialogs = () => {
  addPermissionDialog.value = {
    show: false,
    app: "",
    scopeType: "customer",
    scope: "",
    scopeGroup: "",
    selected: "",
    selectedActions: [],
  };
  addRoleDialog.value = {
    show: false,
    scopeType: "customer",
    scope: "",
    scopeGroup: "",
    selected: "",
  };
};

const resetEditRecord = () => {
  editGroupRecord.value = {
    _id: "",
    name: "",
    groupID: "",
    roles: [],
    permissions: [],
  };
};

// API calls
async function getGroups() {
  loading.value = true;
  try {
    const result = await apiQuery("J24Auth", "groups", { apiAction: "get" });
    groups.value = result;
  } catch (error) {
    console.error("Error fetching groups:", error);
  }
  loading.value = false;
}

async function getRoles() {
  const result = await apiQuery("J24Auth", "roles", { apiAction: "get" });
  roles.value = result.map((r) => ({
    ...r,
    title: r.name,
    value: r.name,
  }));
}

async function getPermissionsAndCustomers() {
  const [permResult, custResult, custGroupResult] = await Promise.all([
    apiQuery("J24Auth", "getpermission", ""),
    apiQuery("J24Auth", "customers", { apiAction: "get" }),
    apiQuery("J24Auth", "customers", { apiAction: "getCustomerGroups" }),
  ]);

  permissions.value = permResult.map((p) => ({
    ...p,
    value: p.permissionName,
    title: p.permissionName,
  }));

  customers.value = [
    { value: "*", title: "* (All Customers)" },
    ...custResult.map((c) => ({
      ...c,
      value: c.name,
      title: c.name,
    })),
  ];

  customerGroups.value = custGroupResult.map((g) => ({
    ...g,
    value: g._id,
    title: g.name,
  }));
}

// Actions
function addPermissionSave() {
  const permission = {
    app: addPermissionDialog.value.app,
    permissionName: addPermissionDialog.value.selected,
    actions: [...addPermissionDialog.value.selectedActions],
  };

  // Add either scope or scopeGroup, but not both
  if (addPermissionDialog.value.scopeType === "customerGroup") {
    permission.scopeGroup = addPermissionDialog.value.scopeGroup;
  } else {
    permission.scope = addPermissionDialog.value.scope;
  }

  editGroupRecord.value.permissions.push(permission);
  resetDialogs();
}

function addRoleSave() {
  const role = roles.value.find((r) => r.name === addRoleDialog.value.selected);
  if (role) {
    const roleEntry = {
      roleID: role._id,
    };

    // Add either scope or scopeGroup, but not both
    if (addRoleDialog.value.scopeType === "customerGroup") {
      roleEntry.scopeGroup = addRoleDialog.value.scopeGroup;
    } else {
      roleEntry.scope = addRoleDialog.value.scope;
    }

    editGroupRecord.value.roles.push(roleEntry);
    resetDialogs();
  }
}

async function newGroupPressed() {
  loading.value = true;
  resetEditRecord();
  await getPermissionsAndCustomers();
  isEditing.value = true;
  loading.value = false;
}

async function editGroupPressed(group) {
  loading.value = true;
  editGroupRecord.value = {
    _id: group._id,
    name: group.name,
    groupID: group.groupID,
    roles: [...group.roles],
    permissions: [...group.permissions],
  };
  await getPermissionsAndCustomers();
  isEditing.value = true;
  loading.value = false;
}

function cancelEdit() {
  resetEditRecord();
  resetDialogs();
  isEditing.value = false;
}

async function saveGroup() {
  loading.value = true;
  const action = editGroupRecord.value._id ? "update" : "add";
  if (action === "add") {
    delete editGroupRecord.value._id;
  }
  await apiQuery("J24Auth", "groups", {
    apiAction: action,
    ...editGroupRecord.value,
  });
  cancelEdit();
  await getGroups();
  loading.value = false;
}

onMounted(() => {
  getGroups();
  getRoles();
});
</script>

<template>
  <v-container>
    <v-row>
      <v-col>
        <h1>Groups Management</h1>
      </v-col>
      <v-col cols="auto">
        <v-btn
          color="primary"
          prepend-icon="mdi-plus"
          @click="newGroupPressed()"
          :disabled="isEditing"
        >
          Add New Group
        </v-btn>
      </v-col>
    </v-row>

    <!-- Edit/Add Form -->
    <v-card v-if="isEditing" class="my-4">
      <v-card-title>
        {{ editGroupRecord._id ? "Edit Group" : "New Group" }}
      </v-card-title>
      <v-card-text>
        <v-form>
          <v-text-field
            v-model="editGroupRecord.name"
            label="Group Name"
            variant="outlined"
            density="comfortable"
          />
          <v-text-field
            v-model="editGroupRecord.groupID"
            label="Group Identifier"
            variant="outlined"
            density="comfortable"
          />

          <!-- Roles Section -->
          <v-divider class="my-4" />
          <div class="d-flex align-center mb-2">
            <h3>Roles</h3>
            <v-spacer />
            <v-btn
              size="small"
              icon="mdi-plus"
              variant="text"
              @click="addRoleDialog.show = true"
            />
          </div>

          <v-data-table
            :headers="[
              { title: 'Scope', key: 'scope' },
              { title: 'Role Name', key: 'roleID' },
              { title: 'Actions', key: 'actions', sortable: false },
            ]"
            :items="editGroupRecord.roles"
            density="compact"
          >
            <template v-slot:[`item.scope`]="{ item }">
              {{ getScopeDisplay(item) }}
            </template>
            <template v-slot:[`item.roleID`]="{ item }">
              {{ getRoleName(item.roleID) }}
            </template>
            <template v-slot:[`item.actions`]="{ index }">
              <v-btn
                size="small"
                icon="mdi-delete"
                variant="text"
                color="error"
                @click="editGroupRecord.roles.splice(index, 1)"
              />
            </template>
            <template v-slot:[`no-data`]>
              <div class="text-center pa-4">No roles added yet</div>
            </template>
          </v-data-table>

          <!-- Permissions Section -->
          <v-divider class="my-4" />
          <div class="d-flex align-center mb-2">
            <h3>Permissions</h3>
            <v-spacer />
            <v-btn
              size="small"
              icon="mdi-plus"
              variant="text"
              @click="addPermissionDialog.show = true"
            />
          </div>

          <v-data-table
            :headers="[
              { title: 'Scope', key: 'scope' },
              { title: 'App', key: 'app' },
              { title: 'Permission', key: 'permissionName' },
              { title: 'Actions', key: 'actions' },
              { title: '', key: 'delete', sortable: false },
            ]"
            :items="editGroupRecord.permissions"
            density="compact"
          >
            <template v-slot:[`item.scope`]="{ item }">
              {{ getScopeDisplay(item) }}
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              {{ item.actions.join(", ") }}
            </template>
            <template v-slot:[`item.delete`]="{ index }">
              <v-btn
                size="small"
                icon="mdi-delete"
                variant="text"
                color="error"
                @click="editGroupRecord.permissions.splice(index, 1)"
              />
            </template>
            <template v-slot:[`no-data`]>
              <div class="text-center pa-4">No permissions added yet</div>
            </template>
          </v-data-table>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="elevated" @click="cancelEdit()">Cancel</v-btn>
        <v-btn variant="elevated" color="primary" @click="saveGroup()"
          >Save Group</v-btn
        >
      </v-card-actions>
    </v-card>

    <!-- Groups List -->
    <v-card v-else>
      <v-card-text>
        <v-text-field
          v-model="searchQuery"
          prepend-inner-icon="mdi-magnify"
          label="Search groups"
          variant="outlined"
          density="comfortable"
          clearable
          hide-details
        />
      </v-card-text>
      <v-data-table
        :headers="[
          { title: 'Group Name', key: 'name' },
          { title: 'Group ID', key: 'groupID' },
          { title: 'Actions', key: 'actions', sortable: false },
        ]"
        :items="groups"
        :loading="loading"
        :search="searchQuery"
      >
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            size="small"
            icon="mdi-pencil"
            variant="text"
            @click="editGroupPressed(item)"
          />
        </template>
      </v-data-table>
    </v-card>

    <!-- Add Role Dialog -->
    <v-dialog v-model="addRoleDialog.show" max-width="500">
      <v-card>
        <v-card-title>Add Role</v-card-title>
        <v-card-text>
          <v-radio-group
            v-model="addRoleDialog.scopeType"
            inline
            label="Scope Type:"
          >
            <v-radio label="Individual Customer" value="customer" />
            <v-radio label="Customer Group" value="customerGroup" />
          </v-radio-group>

          <v-select
            v-if="addRoleDialog.scopeType === 'customer'"
            v-model="addRoleDialog.scope"
            :items="customers"
            label="Customer Scope"
            variant="outlined"
          />
          <v-select
            v-else
            v-model="addRoleDialog.scopeGroup"
            :items="customerGroups"
            label="Customer Group Scope"
            variant="outlined"
          />

          <v-select
            v-model="addRoleDialog.selected"
            :items="roles"
            label="Role"
            variant="outlined"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="elevated" @click="resetDialogs()">Cancel</v-btn>
          <v-btn
            variant="elevated"
            color="primary"
            @click="addRoleSave()"
            :disabled="
              (!addRoleDialog.scope && !addRoleDialog.scopeGroup) ||
              !addRoleDialog.selected
            "
          >
            Add
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Permission Dialog -->
    <v-dialog v-model="addPermissionDialog.show" max-width="600">
      <v-card>
        <v-card-title>Add Permission</v-card-title>
        <v-card-text>
          <v-radio-group
            v-model="addPermissionDialog.scopeType"
            inline
            label="Scope Type:"
          >
            <v-radio label="Individual Customer" value="customer" />
            <v-radio label="Customer Group" value="customerGroup" />
          </v-radio-group>

          <v-select
            v-if="addPermissionDialog.scopeType === 'customer'"
            v-model="addPermissionDialog.scope"
            :items="customers"
            label="Customer Scope"
            variant="outlined"
          />
          <v-select
            v-else
            v-model="addPermissionDialog.scopeGroup"
            :items="customerGroups"
            label="Customer Group Scope"
            variant="outlined"
          />

          <v-select
            v-model="addPermissionDialog.app"
            :items="apps"
            label="App"
            variant="outlined"
          />
          <v-select
            v-model="addPermissionDialog.selected"
            :items="permissionsFiltered"
            label="Permission"
            variant="outlined"
            :disabled="!addPermissionDialog.app"
          />
          <v-combobox
            v-model="addPermissionDialog.selectedActions"
            :items="availableActions"
            label="Actions"
            variant="outlined"
            multiple
            chips
            :disabled="!addPermissionDialog.selected"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="elevated" @click="resetDialogs()">Cancel</v-btn>
          <v-btn
            variant="elevated"
            color="primary"
            @click="addPermissionSave()"
            :disabled="
              (!addPermissionDialog.scope && !addPermissionDialog.scopeGroup) ||
              !addPermissionDialog.app ||
              !addPermissionDialog.selected ||
              !addPermissionDialog.selectedActions.length
            "
          >
            Add
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading Dialog -->
    <v-dialog v-model="loading" persistent max-width="300">
      <v-card color="grey">
        <v-card-text>
          Loading
          <v-progress-linear indeterminate color="white" class="mb-0" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>
